/**
 * ClearStandingDialog (TMMPDM-0037; the TMMPDM-0025 Keep dialog, extracted) — `POST …/standing/clear`
 * with a required 1–500 character reason. tmm-api 0100 allows it from `PROBATION` as well as
 * `DEACTIVATION_DUE`: `keep` is **Keep this reseller…** (clears the deactivation flag), `endProbation`
 * is **End probation…** (the reseller is emailed that it has recovered). Same endpoint, same reason.
 * 409 `RESELLER_STANDING_CONFLICT` (the standing moved) → `onConflict`; 502 outcome-unknown →
 * `onOutcomeUnknown` (re-read, never re-POST); anything else stays in the dialog.
 * TMMPDM-0041 (tmm-api 0102): 409 `RESELLER_SUSPENDED_USE_REACTIVATE` (saas-auth holds the reseller
 * suspended; nothing changed) says clearing isn't enough and offers Reactivate…, which closes this
 * dialog and opens the reactivate one (`onUseReactivate`); the standing is re-read (`onSuspended`).
 * TMMPDM-0046: Reactivate… shows progress until that re-read lands (`standingPending`).
 * TMMPDM-0072 (tmm-api 0120): the CODE picks the copy. 409 `RESELLER_SUSPENDED_USE_REACTIVATE` (a
 * take-over) says use Reactivate…, and `reactivateOffered` alone gates that button (it waits, with
 * progress, until the re-read lands). 409 `RESELLER_SUSPENDED_USE_STATUS` (an ordinary suspension)
 * is catalog copy through `errorCopy`: lift the suspension with Reactivate on its status first. It
 * offers no Reactivate… and re-reads nothing (nothing changed). TMMPDM-0075 A5: that refusal is a
 * `warning`, as the take-over's is (0069); every other refusal stays an `error`.
 */
import { useRef, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy, isOutcomeUnknown } from '@/api/errors';

export type ClearStandingMode = 'keep' | 'endProbation';

interface ClearStandingDialogProps {
  open: boolean;
  mode: ClearStandingMode;
  reseller: { id: number; name: string };
  onClose: () => void;
  onCleared: () => void;
  onConflict: () => void;
  onOutcomeUnknown: () => void;
  /** 409 `RESELLER_SUSPENDED_USE_REACTIVATE` — the parent re-reads the standing; the dialog stays. */
  onSuspended: () => void;
  /** Its Reactivate…: this dialog has closed; the parent opens the reactivate dialog. */
  onUseReactivate: () => void;
  reactivateOffered: boolean;
  /**
   * TMMPDM-0046: the standing is being re-read. Reactivate… waits for it (a progress state), so the
   * reactivate dialog opens knowing the standing — and says when the deactivation window closes.
   */
  standingPending: boolean;
}

/** `ResellerStandingClearRequest.reason`: 1–500 characters (TMMAPI-0084). */
const REASON_MAX = 500;

export function ClearStandingDialog(props: ClearStandingDialogProps) {
  const { open, onClose } = props;
  const [busy, setBusy] = useState(false);
  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!busy) onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      {open ? <ClearStandingForm {...props} busy={busy} setBusy={setBusy} /> : null}
    </Dialog>
  );
}

function ClearStandingForm({
  mode,
  reseller,
  onClose,
  onCleared,
  onConflict,
  onOutcomeUnknown,
  onSuspended,
  onUseReactivate,
  reactivateOffered,
  standingPending,
  busy,
  setBusy,
}: ClearStandingDialogProps & { busy: boolean; setBusy: (busy: boolean) => void }) {
  const { t } = useCopy();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  // The ordinary-suspension refusal says what to do first; it did not fail (TMMPDM-0075 A5).
  const [errorIsWarning, setErrorIsWarning] = useState(false);
  const [suspended, setSuspended] = useState(false);
  const inFlight = useRef(false);
  const trimmed = reason.trim();
  const valid = trimmed.length >= 1 && trimmed.length <= REASON_MAX;
  // While the standing re-reads after the refusal, Reactivate… shows its progress state. Once it
  // lands, `reactivateOffered` alone decides whether the button stays.
  const showReactivate = standingPending || reactivateOffered;

  const submit = async () => {
    if (!valid || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    setErrorIsWarning(false);
    setSuspended(false);
    try {
      await adminApi.clearStanding(reseller.id, trimmed);
      onClose();
      onCleared();
    } catch (err) {
      if (err instanceof ApiError && String(err.code) === 'RESELLER_SUSPENDED_USE_REACTIVATE') {
        setSuspended(true);
        onSuspended();
        return;
      }
      if (err instanceof ApiError && String(err.code) === 'RESELLER_STANDING_CONFLICT') {
        onClose();
        onConflict();
        return;
      }
      if (isOutcomeUnknown(err)) {
        onClose();
        onOutcomeUnknown();
        return;
      }
      setErrorIsWarning(
        err instanceof ApiError && String(err.code) === 'RESELLER_SUSPENDED_USE_STATUS',
      );
      setError(
        errorCopy(
          err,
          t,
          mode === 'keep'
            ? t('resellers.overview.keep.error', 'Could not keep the reseller.')
            : t('resellers.standing.endProbation.error', 'Could not end probation.'),
        ),
      );
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <>
      <DialogTitle>
        {mode === 'keep'
          ? t('resellers.overview.keep.title', 'Keep {{name}}?', { name: reseller.name })
          : t('resellers.standing.endProbation.title', 'End probation for {{name}}?', {
              name: reseller.name,
            })}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity={errorIsWarning ? 'warning' : 'error'}>{error}</Alert> : null}
          {suspended ? (
            <Alert
              severity="warning"
              action={
                showReactivate ? (
                  <Button
                    color="inherit"
                    size="small"
                    loading={standingPending}
                    onClick={() => {
                      onClose();
                      onUseReactivate();
                    }}
                  >
                    {t('resellers.reactivate.button', 'Reactivate…')}
                  </Button>
                ) : undefined
              }
            >
              {t(
                'resellers.overview.keep.suspended',
                "This reseller is suspended in saas-auth, so clearing isn't enough. Use Reactivate… instead.",
              )}
            </Alert>
          ) : null}
          <Typography variant="body2">
            {mode === 'keep'
              ? t(
                  'resellers.overview.keep.body',
                  'Clears the deactivation flag. Its shortfall charges stay open.',
                )
              : t(
                  'resellers.standing.endProbation.body',
                  'Ends probation now. The reseller is emailed that it has recovered.',
                )}
          </Typography>
          <TextField
            label={t('resellers.overview.keep.reason', 'Reason')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            helperText={t('resellers.overview.keep.counter', '{{count}}/{{max}}', {
              count: reason.length,
              max: REASON_MAX,
            })}
            slotProps={{ htmlInput: { maxLength: REASON_MAX } }}
            required
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('resellers.cancel', 'Cancel')}
        </Button>
        <SubmitButton onClick={() => void submit()} busy={busy} disabled={!valid}>
          {mode === 'keep'
            ? t('resellers.overview.keep.confirm', 'Keep this reseller')
            : t('resellers.standing.endProbation.confirm', 'End probation')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
